import { ContentBlock } from '@tytapp/content';
import { ContentBlockType } from '@tytapp/content';
import { ContentBlockEditorComponent } from '@tytapp/content/content-block-editor-component';
import { Component, HostBinding } from '@angular/core';
import { ContentBlockViewComponent } from '@tytapp/content/content-block-view-component';
import { iframeResizer } from 'iframe-resizer';

export interface TextBlock extends ContentBlock {
    body: string;
    columns?: number;
    alignment?: 'left' | 'center' | 'right';
    format?: 'markdown' | 'plain';
}

@Component({
    template: `
        <div
            class="cms-content"
            [attr.data-columns]="block.columns"
            [innerHTML]="block.body | formatToHtml: (block.format ?? 'markdown') | trustHtml"
            ></div>
    `,
    styles: [`
        :host[data-columns="2"] .cms-content { columns: 2; }
        :host[data-columns="3"] .cms-content { columns: 3; }
        :host[data-columns="4"] .cms-content { columns: 4; }
        :host[data-alignment="center"] .cms-content { text-align: center; }
        :host[data-alignment="right"] .cms-content { text-align: right; }
    `]
})
export class ViewComponent extends ContentBlockViewComponent<TextBlock> {
    ngAfterViewInit() {
        setTimeout(() => {
            iframeResizer({ log: true }, 'iframe');
        }, 500);
    }

    @HostBinding('attr.data-alignment') get alignment() { return this.block.alignment; }
    @HostBinding('attr.data-columns') get columns() { return this.block.columns; }
}

@Component({
    template: `
        <div class="controls">
        </div>
        <tyt-markdown-field
            [(value)]="block.body"
            label="Text (Markdown)"
            placeholder="Enter text..."
            [disabled]="locked"
            (keydown)="delayAutoSave()"
            ></tyt-markdown-field>
        <div class="controls">
            <div class="spacer"></div>

            <mat-form-field class="columns" appearance="outline" floatLabel="always">
                <mat-label>Columns</mat-label>
                <input name="maxColumns" type="number" matInput
                    placeholder="1"
                    min="1" max="4" step="1"
                    [(ngModel)]="block.columns"
                    [disabled]="locked" />
            </mat-form-field>

            <mat-button-toggle-group [(ngModel)]="block.alignment" [disabled]="locked" aria-label="Alignment">
                <mat-button-toggle value="left" aria-label="Text align left">
                    <mat-icon>format_align_left</mat-icon>
                </mat-button-toggle>
                <mat-button-toggle value="center" aria-label="Text align center">
                    <mat-icon>format_align_center</mat-icon>
                </mat-button-toggle>
                <mat-button-toggle value="right" aria-label="Text align right">
                    <mat-icon>format_align_right</mat-icon>
                </mat-button-toggle>
            </mat-button-toggle-group>

            <mat-button-toggle-group [(ngModel)]="block.format" [disabled]="locked" aria-label="Format">
                <mat-button-toggle value="markdown" matTooltip="Support Markdown and HTML">
                    Markdown
                </mat-button-toggle>
                <mat-button-toggle value="plain" matTooltip="Plain text, with newlines preserved">
                    Plain
                </mat-button-toggle>
            </mat-button-toggle-group>
        </div>
    `,
    styles: [
        `
        tyt-markdown-field {
            width: 100%;
        }

        mat-form-field.columns {
            width: 5em;
        }
        .controls {
            display: flex;
            align-items: baseline;
            gap: 1em;
        }

        :host[data-alignment="center"] ::ng-deep {
            tyt-markdown-field textarea {
                text-align: center;
            }
        }

        :host[data-alignment="right"] ::ng-deep {
            .heading mat-form-field input[type="text"] {
                text-align: right;
            }

            tyt-markdown-field textarea {
                text-align: right;
            }
        }
        `
    ]
})
export class EditorComponent extends ContentBlockEditorComponent<TextBlock> {
    @HostBinding('attr.data-alignment')
    get alignment() { return this.block.alignment; }
}


export const TEXT_BLOCK_TYPE: ContentBlockType<TextBlock> = {
    id: 'text',
    label: 'Text (Markdown)',
    icon: 'notes',
    template: {
        body: '',
        format: 'markdown'
    },
    preview: {
        body: 'The *quick* **brown** fox jumps [over](https://google.com) the ~~lazy~~ dog'
    },
    previewSettings: {
        horizontalAlign: 'center',
        verticalAlign: 'center'
    },
    editorComponent: EditorComponent,
    viewComponent: ViewComponent
}